import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout/MainLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Wifi } from "lucide-react";
import WiFiStatsCards from "@/components/wifi/WiFiStatsCards";
import WiFiLeadsTable from "@/components/wifi/WiFiLeadsTable";
import WiFiPortalSettings from "@/components/wifi/WiFiPortalSettings";
import SocialConnectSettings from "@/components/wifi/SocialConnectSettings";

const API_PATH = "/wifi";

export default function WiFiMarketingPage() {
  const { authAxios } = useAuth();
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_PATH}/settings`);
      setSettings(r.data);
    } catch (err) {
      console.error("[wifi] settings fetch failed:", err?.response?.status, err?.response?.data, err?.message);
      toast.error("Erro ao carregar configurações WiFi");
    } finally {
      setLoadingSettings(false);
    }
  }, [authAxios]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_PATH}/stats`);
      setStats(r.data);
    } catch (err) {
      console.error("[wifi] stats fetch failed:", err?.message);
    }
  }, [authAxios]);

  const fetchLeads = useCallback(async () => {
    setLoadingLeads(true);
    try {
      const r = await authAxios.get(`${API_PATH}/leads`);
      setLeads(r.data);
    } catch {
      toast.error("Erro ao carregar leads WiFi");
    } finally {
      setLoadingLeads(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchSettings();
    fetchStats();
  }, [fetchSettings, fetchStats]);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const r = await authAxios.patch(`${API_PATH}/settings`, form);
      setSettings(r.data);
      toast.success("Configurações guardadas");
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao guardar";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (tab) => {
    if (tab === "leads") fetchLeads();
  };

  return (
    <MainLayout>
      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Wifi className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Social WiFi Connect</h1>
            <p className="text-sm text-gray-500">
              Capta leads presenciais via QR e entrega a senha do Wi-Fi após cadastro
            </p>
          </div>
        </div>

        <WiFiStatsCards stats={stats} />

        <Tabs defaultValue="social" onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="social">Social Connect</TabsTrigger>
            <TabsTrigger value="leads">Leads</TabsTrigger>
            <TabsTrigger value="advanced">Avançado</TabsTrigger>
          </TabsList>

          <TabsContent value="social" className="mt-4">
            {loadingSettings ? (
              <p className="text-sm text-gray-400 py-6 text-center">A carregar...</p>
            ) : (
              <SocialConnectSettings settings={settings} onSave={handleSave} saving={saving} />
            )}
          </TabsContent>

          <TabsContent value="leads" className="mt-4">
            <WiFiLeadsTable leads={leads} loading={loadingLeads} onRefresh={fetchLeads} />
          </TabsContent>

          <TabsContent value="advanced" className="mt-4">
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              Modo Enterprise — integração com Captive Portal via hardware (UniFi, Omada, MikroTik).
              Para captar leads sem hardware, usa a aba <strong>Social Connect</strong>.
            </div>
            {loadingSettings ? (
              <p className="text-sm text-gray-400 py-6 text-center">A carregar...</p>
            ) : (
              <WiFiPortalSettings settings={settings} onSave={handleSave} saving={saving} />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
